import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, MapPin, Briefcase, User, LogOut } from 'lucide-react';
import { useGDLData } from './useGDLData';
import { Badge } from './GDLComponents';

export default function GDLProfile() {
  const nav = useNavigate();
  const { wallet, csId } = useGDLData();

  if (!csId) { nav('/demo/gdl/login'); return null; }
  if (!wallet) return <SkeletonProfile />;

  const { civil_servant: cs, loan } = wallet;
  const initials = cs.full_name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?';

  const handleLogout = () => {
    localStorage.removeItem('gdl_demo_user');
    localStorage.removeItem('gdl_demo_token');
    localStorage.removeItem('gdl_cache');
    nav('/demo/gdl/login');
  };

  return (
    <div style={{ padding: '12px 16px 24px', display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* Avatar + Name */}
      <div style={{ textAlign: 'center', padding: '12px 0' }}>
        <div style={{
          width: 72, height: 72, borderRadius: '50%',
          background: 'linear-gradient(145deg, #C43B4D, #8E2233)',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 24, fontWeight: 700, color: '#fff', marginBottom: 12,
          boxShadow: '0 6px 20px rgba(176,49,64,0.3)',
        }}>
          {initials}
        </div>
        <div style={{ fontSize: 20, fontWeight: 700, color: '#2D0A10' }}>{cs.full_name}</div>
        <div style={{ fontSize: 14, color: '#A07078', marginTop: 2 }}>Grade Level {cs.grade_level}</div>
        <div style={{ marginTop: 8 }}><Badge variant="primary">GDL+ Member</Badge></div>
      </div>

      {/* Personal Info */}
      <div style={{ background: '#fff', borderRadius: 20, overflow: 'hidden', boxShadow: '0 2px 12px rgba(176,49,64,0.06)' }}>
        <div style={{ padding: '16px 18px', borderBottom: '1px solid rgba(176,49,64,0.06)' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#2D0A10' }}>Personal Information</div>
        </div>
        <Row icon={Phone} label="Phone" value={cs.phone} />
        <Row icon={MapPin} label="State" value={cs.state} />
        <Row icon={Briefcase} label="Ministry" value={cs.ministry} />
        <Row icon={User} label="Grade Level" value={`Level ${cs.grade_level}`} />
      </div>

      {/* Account Info */}
      <div style={{ background: '#fff', borderRadius: 20, overflow: 'hidden', boxShadow: '0 2px 12px rgba(176,49,64,0.06)' }}>
        <div style={{ padding: '16px 18px', borderBottom: '1px solid rgba(176,49,64,0.06)' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#2D0A10' }}>Account Details</div>
        </div>
        <Row label="Account Number" value={cs.account_number} />
        <Row label="Bank" value={cs.bank_name} />
        <Row label="Monthly Salary" value={`\u20A6${cs.monthly_salary?.toLocaleString()}`} />
        {loan && <Row label="Loan Status" value={loan.status?.charAt(0).toUpperCase() + loan.status?.slice(1)} />}
      </div>

      {/* Logout */}
      <button onClick={handleLogout} data-testid="profile-logout" style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        width: '100%', padding: '15px', background: 'rgba(176,49,64,0.06)', color: '#B03140',
        border: 'none', borderRadius: 14, fontSize: 14, fontWeight: 700, cursor: 'pointer',
        transition: 'all 0.15s ease',
      }}>
        <LogOut size={18} /> Sign Out
      </button>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', padding: '13px 18px', borderBottom: '1px solid rgba(176,49,64,0.04)' }}>
      {Icon && <Icon size={16} color="#B03140" style={{ marginRight: 12, flexShrink: 0, opacity: 0.6 }} />}
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 11, color: '#A07078', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
        <div style={{ fontSize: 14, fontWeight: 500, color: '#2D0A10', marginTop: 2 }}>{value || '\u2014'}</div>
      </div>
    </div>
  );
}

function SkeletonProfile() {
  return (
    <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'center' }}>
      <div className="gdl-skeleton" style={{ width: 72, height: 72, borderRadius: '50%' }} />
      <div className="gdl-skeleton" style={{ width: '50%', height: 20 }} />
      <div className="gdl-skeleton" style={{ width: '100%', height: 200, borderRadius: 20 }} />
    </div>
  );
}
